import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PrimaryButton } from '../../components/buttons';
import { EmptyState } from '../../components/empty-state';
import { Loading } from '../../components/loading';
import { OfflineBadge } from '../../components/offline-badge';
import { Row, Table } from '../../components/tables';
import { AlertContext } from '../../contexts/alert-context';
import type { ReceivingSession, ReceivingSessionState } from '../../data/receiving';
import { canCancelSession, makeCompanionBusyPredicate } from '../../data/receiving';
import { receivingSessionUpdatedPayloadSchema } from '../../data/receiving-ws';
import type { Companion } from '../../data/uploader-connection';
import { logUnexpectedApiError } from '../../helpers/api-errors';
import { useCompanionConnections } from '../../hooks/uploads';
import { useVendor } from '../../hooks/vendors';
import { receivingSessionsQueryOptions } from '../../query/query-options';
import { storeLayout } from '../../router';
import { useStoreWebSocket } from '../../websocket/use-store-websocket';
import { CancelSessionModal } from './components/cancel-session-modal';
import { useReportCancelFailure } from './hooks/use-cancel-session';

// ---------------------------------------------------------------------------
// State badge
// ---------------------------------------------------------------------------

const STATE_STYLES: Record<ReceivingSessionState, string> = {
  selecting_companion: 'bg-gray-100 text-gray-800',
  selecting_vendor: 'bg-gray-100 text-gray-800',
  get_invoices: 'bg-blue-100 text-blue-800',
  processing: 'bg-yellow-100 text-yellow-800',
  review: 'bg-blue-100 text-blue-800',
  sending_to_lpos: 'bg-yellow-100 text-yellow-800',
  paused_lpos_busy: 'bg-orange-100 text-orange-800',
  waiting_for_lpos: 'bg-yellow-100 text-yellow-800',
  complete: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATE_LABELS: Record<ReceivingSessionState, string> = {
  selecting_companion: 'Select Companion',
  selecting_vendor: 'Selecting Vendor',
  get_invoices: 'Get Invoices',
  processing: 'Processing',
  review: 'Review',
  sending_to_lpos: 'Sending to LPOS',
  paused_lpos_busy: 'Paused: File In Use',
  waiting_for_lpos: 'Receiving in LPOS',
  complete: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

function StateBadge({ state }: { state: ReceivingSessionState }) {
  const style = STATE_STYLES[state] ?? 'bg-gray-100 text-gray-800';
  const label = STATE_LABELS[state] ?? state;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${style}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Session row
// ---------------------------------------------------------------------------

const cellClassName = 'whitespace-nowrap px-1 py-2 text-sm text-gray-700 font-mono';

function SessionRow({
  session,
  storeId,
  machineIdToHostname,
  liveMachineIds,
  hasAvailableCompanion,
  onRequestCancel,
}: {
  session: ReceivingSession;
  storeId: string;
  machineIdToHostname: Map<string, string>;
  liveMachineIds: Set<string>;
  hasAvailableCompanion: boolean;
  onRequestCancel: (session: ReceivingSession) => void;
}) {
  const { vendor } = useVendor(session.vendor_id);

  // Hostname can be missing if the companion has never been online this
  // session — fall back to the raw machine_id. Vendors are prefetched by the
  // route loader so vendor is expected to be loaded once vendor_id is set.
  const companionLabel = session.machine_id
    ? (machineIdToHostname.get(session.machine_id) ?? session.machine_id)
    : '—';
  const companionConnected = session.machine_id ? liveMachineIds.has(session.machine_id) : false;
  // A session that has chosen a companion can only resume when that companion
  // is back online. A pre-companion session (selecting_companion) has no
  // companion yet, so it resumes whenever *any* companion is free to pick.
  const canResume = session.machine_id ? companionConnected : hasAvailableCompanion;
  const resumeBlockedReason = session.machine_id
    ? 'Companion offline'
    : 'No companion computer available';
  const canCancel = canCancelSession(session.state, session.paused_from);

  return (
    <Row>
      <td className={cellClassName}>
        <StateBadge state={session.state} />
      </td>
      <td className={cellClassName}>
        <span>{companionLabel}</span>
        {session.machine_id && !companionConnected && <OfflineBadge className="ml-2" />}
      </td>
      <td className={cellClassName}>{vendor?.name ?? ''}</td>
      <td className={`${cellClassName} text-right`}>
        <div className="flex items-center justify-end gap-4">
          {canCancel && (
            <button
              type="button"
              onClick={() => onRequestCancel(session)}
              className="text-red-600 hover:text-red-800 font-medium"
            >
              Cancel
            </button>
          )}
          {canResume ? (
            <Link
              to="/stores/$storeId/receiving/$sessionId"
              params={{ storeId, sessionId: session.session_id }}
              className="text-primary-600 hover:text-primary-800 font-medium"
            >
              Resume
            </Link>
          ) : (
            <span
              className="text-gray-400 font-medium cursor-not-allowed"
              title={resumeBlockedReason}
            >
              Resume
            </span>
          )}
        </div>
      </td>
    </Row>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const HEADINGS = ['State', 'Companion', 'Vendor', ''];

export function ReceivingListPage() {
  const { store, receivingService } = storeLayout.useRouteContext();
  const navigate = useNavigate();
  const { addErrorAlert } = useContext(AlertContext);
  const [starting, setStarting] = useState(false);

  const queryClient = useQueryClient();
  const { subscribe } = useStoreWebSocket();

  const { connections } = useCompanionConnections();
  const companions = connections.filter((c): c is Companion => Boolean(c.uploader?.machine_id));
  const hasCompanions = companions.length > 0;

  // Sticky hostname cache: once we've learned a machine_id → hostname mapping
  // we keep it, so a row doesn't regress to the raw machine_id if the
  // companion drops off WebSocket mid-session. Rebuilt each render from the
  // ref so child components always see the accumulated view.
  const stickyHostnamesRef = useRef(new Map<string, string>());
  const machineIdToHostname = useMemo(() => {
    for (const c of companions) {
      if (c.hostname) {
        stickyHostnamesRef.current.set(c.uploader.machine_id, c.hostname);
      }
    }

    return new Map(stickyHostnamesRef.current);
  }, [companions]);

  const liveMachineIds = useMemo(
    () => new Set(companions.map((c) => c.uploader.machine_id)),
    [companions]
  );

  const { data: sessions = [], isLoading } = useQuery({
    ...receivingSessionsQueryOptions(store.id),
    queryFn: () => receivingService.listActiveSessions(store.id),
  });

  // A companion is unavailable while it already has an active session or its
  // disk still holds a pending LPOS import. "Start new receiving" and Resume
  // for pre-companion sessions both need at least one free companion —
  // otherwise the session would strand in the picker with no pickable option.
  const isCompanionBusy = makeCompanionBusyPredicate(sessions);
  const hasAvailableCompanion = companions.some((c) => !isCompanionBusy(c));

  // Keep the session list live: invalidate whenever any session changes.
  useEffect(() => {
    return subscribe('receiving_session_updated', receivingSessionUpdatedPayloadSchema, () => {
      queryClient.invalidateQueries({ queryKey: receivingSessionsQueryOptions(store.id).queryKey });
    });
  }, [subscribe, queryClient, store.id]);

  async function handleStartSession() {
    setStarting(true);
    try {
      const session = await receivingService.startSession(store.id);
      queryClient.invalidateQueries({ queryKey: receivingSessionsQueryOptions(store.id).queryKey });
      await navigate({
        to: '/stores/$storeId/receiving/$sessionId',
        params: { storeId: store.id, sessionId: session.session_id },
      });
    } catch (err) {
      logUnexpectedApiError('receiving: start session failed', err);

      addErrorAlert("Couldn't start a new receiving session. Please try again.", { cause: err });
    } finally {
      setStarting(false);
    }
  }

  const reportCancelFailure = useReportCancelFailure('receiving: cancel failed (sessions list)');
  const [cancelTarget, setCancelTarget] = useState<ReceivingSession | null>(null);
  const dismissCancelConfirm = () => setCancelTarget(null);
  async function handleCancelSession() {
    if (!cancelTarget) {
      return;
    }

    try {
      await receivingService.cancel(store.id, cancelTarget.session_id);
      queryClient.invalidateQueries({ queryKey: receivingSessionsQueryOptions(store.id).queryKey });
      setCancelTarget(null);
    } catch (err) {
      // Leave the confirm modal open on failure so the owner can retry or
      // dismiss — except when another tab holds the session, where retrying
      // from here can't work and the modal would only be in the way.
      if (reportCancelFailure(err)) {
        setCancelTarget(null);
      }
    }
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <h1 className="text-3xl font-semibold text-gray-900">Receiving</h1>
          <PrimaryButton
            disabled={!hasAvailableCompanion || starting}
            onClick={() => void handleStartSession()}
          >
            {starting ? 'Starting...' : 'Start new receiving'}
          </PrimaryButton>
        </div>

        {!hasCompanions && (
          <p className="mt-4 mb-8 text-base leading-relaxed text-gray-600">
            To start a new receiving session, open the{' '}
            <span className="font-medium text-gray-800">Orderbot Companion</span> app on one of your
            store's computers.
          </p>
        )}

        {hasCompanions && !hasAvailableCompanion && (
          <p className="mt-4 mb-8 text-base leading-relaxed text-gray-600">
            All of your store's computers are currently busy receiving. Finish or cancel an active
            session below to start another.
          </p>
        )}

        {sessions.length === 0 ? (
          <EmptyState
            icon={<InboxArrowDownIcon className="size-8" />}
            title="No active receiving sessions"
            description="Sessions you start appear here while they're in progress."
          />
        ) : (
          <Table headings={HEADINGS}>
            {sessions.map((session) => (
              <SessionRow
                key={session.session_id}
                session={session}
                storeId={store.id}
                machineIdToHostname={machineIdToHostname}
                liveMachineIds={liveMachineIds}
                hasAvailableCompanion={hasAvailableCompanion}
                onRequestCancel={setCancelTarget}
              />
            ))}
          </Table>
        )}
      </div>
      <CancelSessionModal
        visible={cancelTarget !== null}
        onClose={dismissCancelConfirm}
        onConfirm={handleCancelSession}
        body="Are you sure you want to cancel this session? Uploaded invoices will be discarded."
      />
    </div>
  );
}
